import { yupResolver } from '@hookform/resolvers/yup';
import React from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { generatePath } from 'react-router';

import {
  AddressDto,
  CreateIndividualCustomerRequestDto,
  CustomerPropertiesDto,
  CustomerTypeDto,
  IndividualCustomerDto,
} from '@/api/generated';
import { ViewWrapper } from '@/components/viewWrapper/ViewWrapper.comp.tsx';
import { RouteEnum } from '@/models/route/Route.model.ts';
import { flagOptions } from '@/utils/flagOptions.util.ts';
import { useAppNavigation } from '@/utils/useAppNavigation.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { CustomersIndividualCreateEditForm } from '@/views/customers/createIndividual/components/CustomersIndividualCreateEditForm.comp';
import { CreateIndividualCustomerFormModel } from '@/views/customers/createIndividual/model/CreateIndividualCustomerForm.model.ts';
import { useCreateIndividualCustomer } from '@/views/customers/createIndividual/util/useCreateIndividualCustomer.util.ts';
import { useCreateIndividualCustomerFormValidation } from '@/views/customers/createIndividual/util/useCreateIndividualCustomerForm.validation.ts';
import { useUpdateIndividualCustomer } from '@/views/customers/createIndividual/util/useUpdateIndividualCustomer.util.ts';
import { customerFormSubmitUtil } from '@/views/customers/status/customerFormSubmit.util.ts';
import { useCustomerStatusField } from '@/views/customers/status/useCustomerStatusField.util.ts';

type Props = {
  customerDetails?: IndividualCustomerDto;
  onCancel?: () => void;
  onPostCreateAction?: (customer: CustomerPropertiesDto) => void;
};

const CustomersCreateIndividualView: React.FC<Props> = ({
  customerDetails,
  onCancel,
  onPostCreateAction,
}) => {
  const validation = useCreateIndividualCustomerFormValidation();
  const { createIndividualCustomer, isCreatingIndividualCustomer } = useCreateIndividualCustomer();
  const { isUpdatingIndividualCustomer, updateIndividualCustomer } = useUpdateIndividualCustomer();
  const { t } = useTranslationWithPrefix('views.customersIndividualCreate');
  const { t: dictT } = useTranslationWithPrefix('utils.dictionaries');
  const { push } = useAppNavigation();
  const statusField = useCustomerStatusField(customerDetails?.status);

  const formMethods = useForm<CreateIndividualCustomerFormModel>({
    defaultValues: customerDetails
      ? {
          ...customerDetails,
          ...customerDetails.address,
          flag: flagOptions(dictT).find(it => it.value === customerDetails.flag),
          nip: customerDetails.nip ?? undefined,
          status: statusField.defaultOption,
        }
      : { status: statusField.defaultOption },
    resolver: yupResolver(validation),
  });

  const handleSubmit: SubmitHandler<CreateIndividualCustomerFormModel> = async data => {
    const { distinguishingLabel, flag, name, nip, status, ...address } = data;
    const dto: CreateIndividualCustomerRequestDto = {
      address: address as Partial<AddressDto>,
      distinguishingLabel,
      flag: data.flag.value,
      name,
      nip,
      status: status?.value,
    };
    const openDetails = (customerId: number) =>
      push(generatePath(RouteEnum.CUSTOMERS_DETAILS, { customerId: customerId.toString() }));
    if (!customerDetails) {
      await customerFormSubmitUtil.create({
        create: () => createIndividualCustomer(dto),
        dto,
        onPostCreateAction,
        openDetails,
        type: CustomerTypeDto.Individual,
      });
    } else {
      await customerFormSubmitUtil.update({
        customerId: customerDetails.customerId,
        openDetails,
        update: () =>
          updateIndividualCustomer({
            customerId: customerDetails.customerId,
            data: {
              ...dto,
              customerId: customerDetails.customerId,
              status: dto.status ?? customerDetails.status,
              type: customerDetails.type,
            },
          }),
      });
    }
  };

  const disabled = isCreatingIndividualCustomer || isUpdatingIndividualCustomer;
  return (
    <ViewWrapper sx={{ px: 5, py: 3 }}>
      <CustomersIndividualCreateEditForm
        disabled={disabled}
        formMethods={formMethods}
        isEdit={Boolean(customerDetails)}
        onCancel={onCancel}
        onSubmit={handleSubmit}
        statusField={statusField}
        t={t}
        validation={validation}
      />
    </ViewWrapper>
  );
};

export default CustomersCreateIndividualView;
